import ActionButton from "@/components/buttons/ActionButton";
import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import FileTypeIconWithFilename from "@/components/icons/FileTypeIconWithFilename";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import StatusTag from "@/components/tags/StatusTag";
import { selectStandardProps, tableStandardProps } from "@/utils/componentUtils";
import { formatFileSize, formatLocaleCurrency } from "@/utils/formatUtils";
import { isDefined } from "@/utils/utils";
import { Col, Divider, Form, Popconfirm, Row, Select, Table, Upload } from "antd";
import { ColumnsType } from "antd/lib/table";
import { RcFile } from "antd/lib/upload";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import {
  useDeleteCommissionsBatchInput,
  useEnumeration,
  useReplaceCommissionsBatchInput,
  useUploadCommissionsBatchInput,
} from "../../../../../common/api/queries";
import { FileUploadAccept, rowGutter, TableSizes } from "../../../../../common/constants";
import { Permission } from "../../../../../common/security/enums";
import { useHasPermissions, useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { EnumerationType } from "../../../../enumerations/enums";
import { CommissionsBatchStep } from "../../enums";
import { COMMISSIONS_BATCH_ROUTE_PATHS } from "../../paths";
import { CommissionsBatch, CommissionsInput, CommissionsUnit } from "../../types";
import { isCommissionAdvanceBatch } from "../../utils";

interface Props {
  batch: CommissionsBatch;
  unit: CommissionsUnit;
}

enum FileUploadType {
  SOURCE = "SOURCE",
  DEFAULT_SOURCE = "DEFAULT_SOURCE",
  ATTACHMENT = "ATTACHMENT",
}

export const CommissionsUnitInputsTable = ({ batch, unit }: Props) => {
  const { t } = useTranslation();

  const uploadCommissionsBatchInput = useUploadCommissionsBatchInput();
  const replaceCommissionsBatchInput = useReplaceCommissionsBatchInput();
  const deleteCommissionsBatchInput = useDeleteCommissionsBatchInput();

  const commissionTemplateEnums = useEnumeration(EnumerationType.COMMISSION_SOURCE_TEMPLATES);
  const isTopAgentPlus = useIsCurrentAgentTypePlus();
  const showAdvanceAmounts = isCommissionAdvanceBatch(batch.type, isTopAgentPlus);

  const hasManageCommissionsPermission = useHasPermissions(Permission.COMMISSIONS_MANAGE);

  const institutionTemplateColumnsNames = useMemo(
    () => commissionTemplateEnums.find((template) => template.institutionId === unit.institution.id)?.expectedColumns,
    [commissionTemplateEnums, unit.institution.id],
  );

  const defaultTemplateColumnsNames = useMemo(
    () => commissionTemplateEnums.find((template) => !template.institutionId)?.expectedColumns,
    [commissionTemplateEnums],
  );

  const [uploadType, setUploadType] = useState<FileUploadType>(
    institutionTemplateColumnsNames ? FileUploadType.SOURCE : FileUploadType.DEFAULT_SOURCE,
  );

  const handleInputsUpload = (file: RcFile, fileList: RcFile[], replacedInput?: CommissionsInput) => {
    if (fileList.length > 0) {
      const formData = new FormData();
      formData.append("file", file);

      if (replacedInput) {
        formData.append("source", replacedInput.source.toString());
        formData.append("forceDefaultImporter", replacedInput.defaultSource.toString());
        replaceCommissionsBatchInput.mutate({ id1: batch.id, id2: replacedInput.id, object: formData });
      } else {
        formData.append("institutionId", unit.institution.id);
        formData.append(
          "source",
          (uploadType === FileUploadType.SOURCE || uploadType === FileUploadType.DEFAULT_SOURCE).toString(),
        );
        formData.append("forceDefaultImporter", (uploadType === FileUploadType.DEFAULT_SOURCE).toString());
        uploadCommissionsBatchInput.mutate({ id: batch.id, object: formData });
      }

      fileList.splice(0, 1);
    }

    return false;
  };

  const resolveFileUploadTypeLabel = () => {
    if (uploadType === FileUploadType.ATTACHMENT) {
      return t("commissions.batch.enums.fileUploadType._label");
    }

    const popoverTitle =
      uploadType === FileUploadType.SOURCE
        ? t("commissions.batch.helpers.sourceAttachmentHint")
        : t("commissions.batch.helpers.defaultSourceAttachmentHint");
    const columnsNames =
      uploadType === FileUploadType.SOURCE ? institutionTemplateColumnsNames : defaultTemplateColumnsNames;

    return (
      <LabelWithPopover
        label={t("commissions.batch.enums.fileUploadType._label")}
        popoverTitle={popoverTitle}
        popoverContent={
          <div style={{ maxWidth: "400px" }}>
            <Row gutter={rowGutter}>
              {columnsNames?.map((column) => (
                <Col key={column} span={12}>
                  {column}
                </Col>
              ))}
            </Row>

            {uploadType !== FileUploadType.SOURCE && (
              <div>
                <div className="margin-top-small margin-bottom-tiny">
                  {t("commissions.batch.helpers.commissionKidColumnValue")}:
                </div>

                <Table
                  {...tableStandardProps()}
                  pagination={false}
                  size="small"
                  showHeader={false}
                  scroll={{ y: 150 }}
                  columns={[
                    { dataIndex: "abbreviation", key: 1, width: 80 },
                    { dataIndex: "label", key: 2, width: 250 },
                    { dataIndex: "type", key: 3, width: 55 },
                  ]}
                  dataSource={
                    isTopAgentPlus
                      ? [
                          { abbreviation: "ZS", label: t("commissions.batch.enums.commissionKind.INITIAL"), type: "Z" },
                          {
                            abbreviation: "ZSD",
                            label: t("commissions.batch.enums.commissionKind.INITIAL_DIVIDED"),
                            type: "Z",
                          },
                          {
                            abbreviation: "IX",
                            label: t("commissions.batch.enums.commissionKind.INDEXATION"),
                            type: "Z",
                          },
                          {
                            abbreviation: "ZP",
                            label: t("commissions.batch.enums.commissionKind.PREMIUM_CHANGE"),
                            type: "Z",
                          },
                          {
                            abbreviation: "PP",
                            label: t("commissions.batch.enums.commissionKind.PREPAID_FEE"),
                            type: "Z",
                          },
                          {
                            abbreviation: "EPP",
                            label: t("commissions.batch.enums.commissionKind.PREPAID_EXPRESS_FEE"),
                            type: "Z",
                          },
                          {
                            abbreviation: "PRP",
                            label: t("commissions.batch.enums.commissionKind.ONGOING_FEE"),
                            type: "Z",
                          },
                          {
                            abbreviation: "PSZ",
                            label: t("commissions.batch.enums.commissionKind.GRADUAL_PAYMENT"),
                            type: "Z",
                          },
                          {
                            abbreviation: "PS",
                            label: t("commissions.batch.enums.commissionKind.GRADUAL_ADVANCE_PAYMENT"),
                            type: "Z",
                          },
                          { abbreviation: "SP", label: t("commissions.batch.enums.commissionKind.SAVINGS"), type: "Z" },
                          {
                            abbreviation: "NK",
                            label: t("commissions.batch.enums.commissionKind.PURCHASE"),
                            type: "Z",
                          },
                          { abbreviation: "VCN", label: t("commissions.batch.enums.commissionKind.VCN"), type: "Z" },
                          {
                            abbreviation: "ZSE",
                            label: t("commissions.batch.enums.commissionKind.INITIAL_ELECTRONIC"),
                            type: "Z",
                          },
                          {
                            abbreviation: "ZSP",
                            label: t("commissions.batch.enums.commissionKind.INITIAL_PAPER"),
                            type: "Z",
                          },
                          {
                            abbreviation: "NP",
                            label: t("commissions.batch.enums.commissionKind.CONTRIBUTION_INCREASE"),
                            type: "Z",
                          },
                          {
                            abbreviation: "DZ",
                            label: t("commissions.batch.enums.commissionKind.SECOND_CONTRACT"),
                            type: "Z",
                          },
                          {
                            abbreviation: "PRO",
                            label: t("commissions.batch.enums.commissionKind.TRANSFERRED_FUNDS"),
                            type: "Z",
                          },
                          {
                            abbreviation: "NE",
                            label: t("commissions.batch.enums.commissionKind.UNKNOWN"),
                            type: "Z",
                          },
                          {
                            abbreviation: "UD",
                            label: t("commissions.batch.enums.commissionKind.SUBSEQUENT"),
                            type: "U",
                          },
                          { abbreviation: "MA", label: t("commissions.batch.enums.commissionKind.ASSET"), type: "U" },
                          {
                            abbreviation: "DS",
                            label: t("commissions.batch.enums.commissionKind.ADDITIONAL_SAVINGS"),
                            type: "U",
                          },
                          { abbreviation: "VY", label: t("commissions.batch.enums.commissionKind.BUYOUT"), type: "U" },
                          { abbreviation: "SU", label: t("commissions.batch.enums.commissionKind.SUCCESS"), type: "U" },
                          {
                            abbreviation: "ETFS",
                            label: t("commissions.batch.enums.commissionKind.ETF_SUCCESS"),
                            type: "U",
                          },
                          {
                            abbreviation: "TR",
                            label: t("commissions.batch.enums.commissionKind.TRANSACTION"),
                            type: "U",
                          },
                          {
                            abbreviation: "MAN",
                            label: t("commissions.batch.enums.commissionKind.MANAGERIAL"),
                            type: "U",
                          },
                          {
                            abbreviation: "PRFM",
                            label: t("commissions.batch.enums.commissionKind.PRF_MANAGERIAL"),
                            type: "U",
                          },
                          {
                            abbreviation: "ETFM",
                            label: t("commissions.batch.enums.commissionKind.ETF_MANAGERIAL"),
                            type: "U",
                          },
                          {
                            abbreviation: "NA",
                            label: t("commissions.batch.enums.commissionKind.RENEWAL"),
                            type: "N",
                          },
                          {
                            abbreviation: "BO",
                            label: t("commissions.batch.enums.commissionKind.BONUS"),
                            type: "B",
                          },
                          {
                            abbreviation: "BO2",
                            label: t("commissions.batch.enums.commissionKind.BONUS_EXTENDED"),
                            type: "B",
                          },
                        ]
                      : [
                          { abbreviation: "ZS", label: t("commissions.batch.enums.commissionKind.INITIAL"), type: "Z" },
                          {
                            abbreviation: "UD",
                            label: t("commissions.batch.enums.commissionKind.SUBSEQUENT"),
                            type: "U",
                          },
                        ]
                  }
                />
              </div>
            )}
          </div>
        }
      />
    );
  };

  const isReadOnly = batch.step === CommissionsBatchStep.FINISH || !hasManageCommissionsPermission;
  const filenameWidth = isReadOnly ? (showAdvanceAmounts ? 310 : 280) : 190;

  const columns: ColumnsType<CommissionsInput> = [
    {
      key: "filename",
      title: t("common.filename"),
      width: filenameWidth,
      ellipsis: { showTitle: false },
      render: (_, record) =>
        record.file ? (
          <FileTypeIconWithFilename contentType={record.file.contentType} filename={record.file.filename} ellipsis />
        ) : undefined,
    },
    {
      key: "source",
      title: t("commissions.batch.attrs.attachment.source"),
      align: "center",
      width: 110,
      render: (_, record) => (record.source ? <AntIcon type="check" /> : <AntIcon type="close" />),
    },
    {
      key: "importedCommissionAmount",
      title: t("commissions.batch.attrs.attachment.importedCommissionAmount"),
      align: "right",
      width: 110,
      render: (_, record) => (record.source ? formatLocaleCurrency(record.importedCommissionAmount ?? 0) : undefined),
    },
    {
      key: "postponedCommissionAmount",
      title: t("commissions.batch.attrs.attachment.postponedCommissionAmount"),
      align: "right",
      width: 110,
      render: (_, record) => (record.source ? formatLocaleCurrency(record.postponedCommissionAmount ?? 0) : undefined),
    },
  ];

  if (showAdvanceAmounts) {
    columns.push(
      {
        key: "advanceCommissionAmount",
        title: (
          <LabelWithTooltip
            label={t("commissions.batch.attrs.attachment.advanceCommissionAmount")}
            tooltip={t("commissions.batch.helpers.advanceCommissionAmountDesc")}
          />
        ),
        align: "right",
        width: 110,
        render: (_, record) =>
          record.source && isDefined(record.advanceCommissionAmount)
            ? formatLocaleCurrency(record.advanceCommissionAmount)
            : undefined,
      },
      {
        key: "advancePayoutCommissionAmount",
        title: (
          <LabelWithTooltip
            label={t("commissions.batch.attrs.attachment.advancePayoutCommissionAmount")}
            tooltip={t("commissions.batch.helpers.advancePayoutCommissionAmountDesc")}
          />
        ),
        align: "right",
        width: 110,
        render: (_, record) =>
          record.source && isDefined(record.advancePayoutCommissionAmount)
            ? formatLocaleCurrency(record.advancePayoutCommissionAmount)
            : undefined,
      },
    );
  }

  columns.push({
    key: "size",
    title: t("common.size"),
    align: "right",
    width: 110,
    render: (_, record) => (record.file ? formatFileSize(record.file.size) : undefined),
  });

  if (batch.step !== CommissionsBatchStep.FINISH) {
    columns.push({
      key: "statusTags",
      width: 140,
      render: (_, record) => (
        <>
          {record.hasImportErrors && (
            <StatusTag
              status="error"
              tooltip={record.importError?.description || t("commissions.batch.helpers.importedAttachmentError")}
            />
          )}
          {record.importInProgress && <StatusTag status="processing" />}
        </>
      ),
    });
  }

  columns.push({
    key: "actions",
    align: "right",
    fixed: "right",
    width: isReadOnly ? 180 : 370,
    render: (_, record) => {
      const manageable = batch.step !== CommissionsBatchStep.FINISH && hasManageCommissionsPermission;
      const inputDisabled = batch.stepChangeInProgress || record.importInProgress;

      return (
        <>
          {record.source && (
            <>
              <ActionTextIcon
                icon="eye"
                color="green"
                text={t("common.show")}
                disabled={inputDisabled}
                path={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.inputImports.to, {
                  id1: batch.id,
                  id2: record.id,
                })}
              />

              <Divider orientation="vertical" />
            </>
          )}

          <ActionTextIcon
            icon="download"
            color="blue"
            text={t("common.download")}
            disabled={record.importInProgress}
            path={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.input.to, { id1: batch.id, id2: record.id })}
            target="_blank"
          />

          {manageable && (
            <>
              {record.source && (
                <>
                  <Divider orientation="vertical" />

                  <Upload
                    showUploadList={false}
                    accept={FileUploadAccept.CSV}
                    disabled={inputDisabled}
                    beforeUpload={(file, fileList) => handleInputsUpload(file, fileList, record)}
                  >
                    <ActionTextIcon icon="sync" color="orange" text={t("common.replace")} disabled={inputDisabled} />
                  </Upload>
                </>
              )}

              <Divider orientation="vertical" />

              <Popconfirm
                title={t("commissions.batch.helpers.deleteAttachment")}
                icon={<PopconfirmDeleteIcon />}
                okText={t("common.yes")}
                cancelText={t("common.no")}
                okType="danger"
                disabled={inputDisabled}
                onConfirm={() => deleteCommissionsBatchInput.mutate({ id1: batch.id, id2: record.id })}
              >
                <ActionTextIcon icon="delete" color="red" text={t("common.delete")} disabled={inputDisabled} />
              </Popconfirm>
            </>
          )}
        </>
      );
    },
  });

  const inputs = batch.inputs.filter((a) => a.institution.id === unit.institution.id);

  return (
    <>
      <Row gutter={rowGutter}>
        <Col span={24}>
          <b>{t("commissions.batch.helpers.manuallyAddedCommissions")}: </b>

          {isDefined(unit.manuallyAddedCommissionAmount) ? (
            <span>
              {t("common.together")}: {formatLocaleCurrency(unit.manuallyAddedCommissionAmount)} |&nbsp;
              {t("common.postponed")}: {formatLocaleCurrency(unit.manuallyAddedPostponedCommissionAmount ?? 0)}
            </span>
          ) : (
            <span className="sub-header-info dashed">{t("common.none")}</span>
          )}

          <ActionTextIcon
            path={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.manualImports.to, {
              id1: batch.id,
              id2: unit.institution.id,
            })}
            icon="interaction"
            color="blue"
            className="margin-left-small"
            text={batch.step === CommissionsBatchStep.FINISH ? t("common.show") : t("common.manage")}
            disabled={batch.stepChangeInProgress}
          />
        </Col>
      </Row>

      <Row gutter={rowGutter} className="margin-top-medium">
        <Col span={24}>
          <Table<CommissionsInput>
            {...tableStandardProps(false, true, "inner-table")}
            columns={columns}
            scroll={{ x: showAdvanceAmounts ? TableSizes.LARGE : TableSizes.MEDIUM }}
            dataSource={inputs}
            pagination={false}
          />
        </Col>
      </Row>

      {batch.step !== CommissionsBatchStep.FINISH && hasManageCommissionsPermission ? (
        <Form layout="vertical">
          <Row gutter={rowGutter} className="margin-top-medium">
            <Col flex="200px">
              <Form.Item label={resolveFileUploadTypeLabel()}>
                <Select<FileUploadType>
                  {...selectStandardProps}
                  size="small"
                  style={{ width: "270px" }}
                  value={uploadType}
                  options={Object.keys(FileUploadType).map((type) => ({
                    value: type,
                    label: t("commissions.batch.enums.fileUploadType." + type),
                    disabled: type === FileUploadType.SOURCE && !institutionTemplateColumnsNames,
                  }))}
                  onChange={setUploadType}
                />
              </Form.Item>
            </Col>

            <Col flex="140px">
              <Form.Item className="form-item-without-label">
                <Upload
                  showUploadList={false}
                  accept={
                    uploadType === FileUploadType.SOURCE || uploadType === FileUploadType.DEFAULT_SOURCE
                      ? FileUploadAccept.CSV
                      : undefined
                  }
                  disabled={batch.stepChangeInProgress}
                  beforeUpload={handleInputsUpload}
                >
                  <ActionButton icon="plus" disabled={batch.stepChangeInProgress}>
                    {t("commissions.batch.actions.addAttachment")}
                  </ActionButton>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      ) : (
        <Row gutter={rowGutter} className="margin-top-medium" />
      )}
    </>
  );
};
